import { useSuspenseQuery } from '@tanstack/react-query';
import RexService from 'services/rexService';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useCallback, useContext } from 'react';
import {
  ContractDateToUse,
  StatisticsForEntityRequestEntityType,
  StaffGetEntityNumbersResponse,
  StatisticsForEntityRequestTimePeriod,
} from '@urbanx/agx.rex.client';
import { toExclusiveDate } from '@urbanx/agx.ui-components2';

type StaffGetEntityNumbersKey = [
  'StaffGetEntityNumbers',
  string | undefined, // entityId
  ContractDateToUse,
  string, // fromDate
  string, // toDateInclusive
  StatisticsForEntityRequestEntityType, //entityType
  StatisticsForEntityRequestTimePeriod, //timePeriod
];

const useStaffGetEntityNumbers = (
  entityId: string | undefined,
  contractDateToUse: ContractDateToUse,
  fromDate: string,
  toDateInclusive: string,
  entityType: StatisticsForEntityRequestEntityType,
  timePeriod: StatisticsForEntityRequestTimePeriod
): {
  data: StaffGetEntityNumbersResponse;
  isPending: boolean;
  isError: boolean;
} => {
  const { getAuthToken } = useContext(AuthContext);

  const staffGetEntityNumbers = useCallback(
    async ([
      url,
      entityId,
      contractDateToUse,
      fromDate,
      toDateInclusive,
      entityType,
      timePeriod,
    ]: StaffGetEntityNumbersKey): Promise<StaffGetEntityNumbersResponse> => {
      try {
        const token = await getAuthToken();

        const service = new RexService(token);

        const body = {
          entityId: entityId ?? '',
          contractDateToUse,
          fromDate,
          toDateExclusive: toExclusiveDate(toDateInclusive),
          entityType,
          timePeriod,
        };

        const result = await service.rexService.post(url, body);

        return result.data;
      } catch (error) {
        console.error('Error fetching entity numbers:', error);
        throw new Error(
          `Failed to fetch entity numbers for entityId: ${entityId}, fromDate: ${fromDate}, toDateInclusive: ${toDateInclusive}, entityType: ${entityType}, timePeriod: ${timePeriod}`
        );
      }
    },
    [getAuthToken]
  );

  const { data, isPending, isError } = useSuspenseQuery<
    StaffGetEntityNumbersResponse,
    Error,
    StaffGetEntityNumbersResponse,
    StaffGetEntityNumbersKey
  >({
    queryKey: [
      'StaffGetEntityNumbers',
      entityId,
      contractDateToUse,
      fromDate,
      toDateInclusive,
      entityType,
      timePeriod,
    ],
    queryFn: (queryInfo) => staffGetEntityNumbers(queryInfo.queryKey),
  });

  return { data, isPending, isError };
};

export default useStaffGetEntityNumbers;
